import { Board, ConvoyView, Shipment } from '../network/Haulage';

/**
 * Jedno miejsce na wszystko, co dokądś jedzie.
 *
 * **Reguły i wozy w jednym oknie, bo to jedno pytanie**: co, dokąd i czemu.
 * Reguła stoi przy zakładzie i trwa; wóz z niej powstaje i kończy się
 * dostarczeniem — rozdzielenie ich na dwa panele znaczyłoby oglądanie przyczyny
 * osobno od skutku.
 *
 * Panel nie pyta serwera sam z siebie. Dostaje to, co gra przed chwilą
 * przeczytała, i mówi, kiedy chce to przeczytać jeszcze raz — dzięki temu jest
 * widokiem, a nie drugim klientem tego samego API.
 *
 * **Nie ma już własnego przycisku ani własnego rogu ekranu.** Jest zawartością
 * zakładki „Handel" w pasku na dole (`HudBar`), a otwieraniem i zamykaniem
 * rządzi pasek — bo to on pilnuje, żeby otwarty był jeden panel naraz.
 * Odświeżenie przy otwarciu jedzie stąd do paska jako `onRefresh`, nie
 * odwrotnie: panel dalej mówi, kiedy chce świeże dane.
 */

export interface HaulDeps {
  /** Jak nazwać budynek po jego identyfikatorze: nazwa osady albo rodzaj. */
  describe: (building: string) => string;
  /** Jak nazwać surowiec. */
  material: (key: string, amount: number) => string;
  onCancel: (shipment: string) => void;
  /** „Pokaż mi, co jest teraz" — panel prosi, gra odpowiada `set`. */
  onRefresh: () => void;
}

export class HaulPanel {
  private readonly root = document.createElement('div');
  private readonly body = document.createElement('div');

  constructor(private readonly deps: HaulDeps) {
    this.root.className = 'haul';
    this.body.className = 'haul-body';
    this.root.appendChild(this.body);
  }

  /** Element do wpięcia tam, gdzie ma stać — dziś w zakładkę paska HUD. */
  get element(): HTMLElement {
    return this.root;
  }

  /**
   * Odśwież, bo panel właśnie się otworzył.
   *
   * Wozy jadą, gdy nikt na nie nie patrzy, więc to, co zostało w panelu
   * z poprzedniego razu, jest nieprawdą. Wołane przez pasek, który wie, kiedy
   * ta zakładka wychodzi na wierzch.
   */
  opened(): void {
    this.deps.onRefresh();
  }

  /** Co gra przed chwilą przeczytała. */
  set(board: Board): void {
    this.body.replaceChildren(this.rules(board.shipments), this.rolling(board.convoys));
  }

  private rules(shipments: Shipment[]): HTMLElement {
    const section = document.createElement('section');
    section.className = 'haul-section';
    section.appendChild(heading('Zlecenia'));

    if (shipments.length === 0) {
      section.appendChild(
        note('brak — zlecenie stawia się w menu zakładu, pod prawym przyciskiem')
      );
      return section;
    }

    for (const shipment of shipments) {
      const row = document.createElement('div');
      row.className = 'haul-row';

      const what = document.createElement('span');
      what.className = 'haul-what';
      what.textContent = `${this.deps.describe(shipment.source)} → ${this.deps.describe(
        shipment.target
      )}`;

      const when = document.createElement('span');
      when.className = 'haul-when';
      // Dwa zdania w jednej linii, bo są o dwóch różnych rzeczach: od ilu wóz
      // rusza i ile zabiera. „Wszystko" nie jest liczbą, więc nie udaje jej.
      when.textContent =
        shipment.amount === undefined
          ? `od ${this.deps.material(shipment.material, shipment.atLeast)}`
          : `od ${this.deps.material(shipment.material, shipment.atLeast)}, po ${
              shipment.amount
            }`;

      const drop = document.createElement('button');
      drop.type = 'button';
      drop.className = 'haul-drop';
      drop.textContent = 'zdejmij';
      drop.addEventListener('click', () => this.deps.onCancel(shipment.id));

      row.append(what, when, drop);
      section.appendChild(row);
    }

    return section;
  }

  private rolling(convoys: ConvoyView[]): HTMLElement {
    const section = document.createElement('section');
    section.className = 'haul-section';
    section.appendChild(heading('W drodze'));

    if (convoys.length === 0) {
      section.appendChild(note('nic nie jedzie'));
      return section;
    }

    for (const convoy of convoys) {
      const row = document.createElement('div');
      row.className = 'haul-row';

      const what = document.createElement('span');
      what.className = 'haul-what';
      what.textContent = 'wóz';

      const left = document.createElement('span');
      left.className = 'haul-when';
      // Pola, a nie minuty: ile to potrwa, zależy od tego, co pod kołami —
      // a liczba pól jest tym, co widać na mapie.
      left.textContent = convoy.left > 0 ? `${convoy.left} pól do celu` : 'dojeżdża';

      row.append(what, left);
      section.appendChild(row);
    }

    return section;
  }

  dispose(): void {
    this.root.remove();
  }
}

function heading(text: string): HTMLElement {
  const head = document.createElement('div');
  head.className = 'haul-heading';
  head.textContent = text;
  return head;
}

function note(text: string): HTMLElement {
  const line = document.createElement('div');
  line.className = 'haul-note';
  line.textContent = text;
  return line;
}
